import { useCallback, useEffect } from 'react';
import { useScreenSize } from '@banhala/ui-kit';
import { type View } from 'react-native';
import {
  measure,
  useAnimatedRef,
  useFrameCallback,
  useSharedValue,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { useIsForegroundNow } from 'shared/src/hooks';
import { retainedState, useRetainedState } from 'shared/src/hooks/retainedState';

const THROTTLE_MS = 100;
const MIN_VIEW_TIME_MS = 500;

// 이번 visit 에 노출된 섹션 key 집합 (remount 가로질러 DISPLAY 재발화 방지).
const sectionVisibleState = retainedState<Set<string>>(() => new Set());

export const useIsSectionVisible = (key: string) => {
  const [visible] = useRetainedState(sectionVisibleState);
  return visible.has(key);
};

export const useMarkSectionVisible = () => {
  const [, setVisible] = useRetainedState(sectionVisibleState);
  return useCallback(
    (key: string) => {
      // 동일 참조 반환 시 useSyncExternalStore 가 리렌더 생략. 변경 시에만 새 Set.
      setVisible(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
    },
    [setVisible],
  );
};

/**
 * 컴포넌트가 뷰포트에 진입했는지 감지하는 훅.
 * 중첩된 서브 섹션 컴포넌트에서 사용.
 *
 * 측정·dwell 판정을 전부 UI 스레드에서 수행한다(useFrameCallback + reanimated measure).
 * 연속 MIN_VIEW_TIME_MS 동안 가시 상태였으면 mark — "잠깐 스쳐 지나간" 케이스에서
 * 가짜 DISPLAY가 발화되는 것을 방지. 중간에 viewport를 벗어나면 visibleStart 리셋.
 * 100ms throttle, mark 이후 SharedValue 게이트로 즉시 early return.
 *
 * ⚠️ 메모리: viewRef 는 useAnimatedRef(=tag 기반 shareable)라 worklet 이 캡처해도
 * 컴포넌트(fiber)를 retain 하지 않는다. JS 로 넘기는 건 markVisible(setVisible)뿐이라
 * `__remoteFunctionRegistry` 가 트리를 붙들지 않는다.
 * (이전 구현은 `scheduleOnRN(checkVisibility)` 가 viewRef→View→fiber 를 캡처해
 *  PDP 트리 전체가 누수됐음 — 섹션 수만큼 ×N)
 */
export const useElementViewportTracking = (
  visibilityKey: string,
  offset = 0,
) => {
  const { height: screenHeight } = useScreenSize();
  const viewRef = useAnimatedRef<View>();
  const markVisible = useMarkSectionVisible();
  const isMarkedSV = useSharedValue(false);
  const visibleStartSV = useSharedValue(0);
  const lastCheckSV = useSharedValue(0);

  const frameCallback = useFrameCallback(frameInfo => {
    if (isMarkedSV.value) {
      return;
    }
    const now = frameInfo.timestamp;
    if (now - lastCheckSV.value < THROTTLE_MS) {
      return;
    }
    lastCheckSV.value = now;
    // 뷰가 아직 레이아웃 안 된 프레임엔 native measure 가 throw 할 수 있어 가드.
    // (worklet 에선 animatedRef.current 가 신뢰 불가 → try/catch 로 방어)
    let measured: ReturnType<typeof measure> = null;
    try {
      measured = measure(viewRef);
    } catch (_error) {
      return;
    }
    if (measured === null) {
      return;
    }
    const isInViewport =
      measured.height > 0 &&
      measured.pageY + measured.height > 0 &&
      measured.pageY < screenHeight - offset;

    if (!isInViewport) {
      visibleStartSV.value = 0;
      return;
    }
    if (visibleStartSV.value === 0) {
      visibleStartSV.value = now;
    } else if (now - visibleStartSV.value >= MIN_VIEW_TIME_MS) {
      isMarkedSV.value = true;
      scheduleOnRN(markVisible, visibilityKey);
    }
  });

  // 화면이 가려지면(다른 화면 push / 백그라운드) frame callback 을 멈춘다.
  // 가려진 동안 measure()가 not-rendered 뷰를 계속 측정하면 reanimated 가 undefined
  // LayoutMetrics 경고를 쏟다가 끝내 libworklets 네이티브 assert(getObject)로 크래시한다.
  // (JS try/catch 는 네이티브 abort 를 못 막는다.) 노출 판정은 화면이 보일 때만 의미 있으므로
  // 가려진 동안 비활성화해도 무방하고, CPU(매 프레임 measure)도 아낀다.
  const isForeground = useIsForegroundNow();
  useEffect(() => {
    frameCallback.setActive(isForeground);
  }, [frameCallback, isForeground]);

  return viewRef;
};
